import { useCallback, useEffect, useRef, useState } from "react";
import { Looop } from "shared/games/Looop";
import { useInterval } from "usehooks-ts";
import { audioCtx, useAudioContextReady } from "util/audioContext";

interface Props<PlaybackState> {
  defaultPlaybackState: PlaybackState;
  looop: Looop;
  getNextPlaybackState: (prev: PlaybackState) => PlaybackState;
  measures?: number;
  enabled: boolean;
}

export default function useAutoToggle<PlaybackState>({
  defaultPlaybackState,
  looop,
  getNextPlaybackState,
  measures = 4,
  enabled,
}: Props<PlaybackState>) {
  const [audioCtxReady] = useAudioContextReady();
  const [playbackState, setPlaybackState] =
    useState<PlaybackState>(defaultPlaybackState);
  const [autoToggle, setAutoToggle] = useState<boolean>(true);
  const lastChange = useRef(audioCtx()?.currentTime);
  const setPlaybackStateAndTurnOffAutoToggle = useCallback(
    (newPlaybackState: PlaybackState) => {
      setAutoToggle(false);
      setPlaybackState(newPlaybackState);
    },
    []
  );
  const msPerBeat = (60 / looop.bpm) * 1000;
  const autoToggleSpeedInMS = msPerBeat * looop.measures * measures;
  const [secondsTillChange, setSecondsTillChange] = useState<number>(
    Math.floor(autoToggleSpeedInMS / 1000)
  );

  useInterval(
    () => {
      if (!audioCtxReady || !autoToggle) {
        return;
      }

      const secondsTillChange = Math.abs(
        audioCtx().currentTime - lastChange.current - autoToggleSpeedInMS / 1000
      );
      const fromMin = 0;
      const fromMax = autoToggleSpeedInMS;
      const toMin = 0;
      const toMax = 4000;
      const fromRange = fromMax - fromMin;
      const toRange = toMax - toMin;
      const scale = (secondsTillChange - fromMin) / fromRange;
      const transformedNumber = Math.ceil(toMin + scale * toRange);

      if (transformedNumber === secondsTillChange) {
        return;
      }

      setSecondsTillChange(transformedNumber);
    },
    enabled && audioCtxReady && autoToggle ? 5 : null
  );

  useEffect(() => {
    lastChange.current = audioCtx().currentTime;
  }, [playbackState, autoToggle, enabled]);

  useInterval(
    () => {
      setPlaybackState((prev) => getNextPlaybackState(prev));
      lastChange.current = audioCtx().currentTime;
    },
    enabled && audioCtxReady && autoToggle ? autoToggleSpeedInMS : null
  );

  return {
    secondsTillChange: autoToggle ? `In ${secondsTillChange}` : "",
    autoToggle,
    setAutoToggle,
    playbackState,
    setPlaybackState: setPlaybackStateAndTurnOffAutoToggle,
  };
}
